import { FC, LegacyRef, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { FieldError } from 'react-hook-form'

import { useQuery } from '@tanstack/react-query'

import { Button, Cluster, PlusIcon, Search, SearchItemType } from '@forto-dev/phoenix-ui-core'

import { useLocalisation } from '@/utils/localisation'

import { locationsQueries } from '../api/locations.queries'
import { useAddressSelectorContext } from '../contexts/useAddressSelectorContext'
import { useAddressSelectorDispatchContext } from '../contexts/useAddressSelectorDispatchContext'
import { getCombinedErrorMessages } from '../utils/getCombinedErrorMessages'

export const AddressSearch: FC = () => {
  const { label, disabled, required, inputRef, manualMode, error } = useAddressSelectorContext()
  const { onAddressSearchChange, onAddManualAddress } = useAddressSelectorDispatchContext()
  const [selectedItem, setSelectedItem] = useState<SearchItemType>()
  const [inputValue, setInputValue] = useState<string>('')
  const { translate, isValidTranslationKey } = useLocalisation()

  const { localAddress: _, ...addressError } = (error ?? {}) as Record<string, FieldError | undefined>
  const errorMessage = useMemo(() => {
    if (manualMode) {
      return undefined
    }

    const combinedErrorMessages = getCombinedErrorMessages(addressError as unknown as FieldError | undefined)
    return !combinedErrorMessages
      ? undefined
      : isValidTranslationKey(combinedErrorMessages)
        ? translate(combinedErrorMessages)
        : combinedErrorMessages
  }, [manualMode, addressError, isValidTranslationKey, translate])
  const searchFieldRef = useRef<HTMLInputElement | null>(null)

  useImperativeHandle(inputRef, () => {
    return {
      focus() {
        if (!errorMessage) {
          return
        }

        searchFieldRef.current?.focus()
      },
    }
  }, [errorMessage])

  const { data: { items: options } = { items: [] }, isLoading } = useQuery({
    ...locationsQueries.list(inputValue),
    enabled: !!inputValue && selectedItem?.label !== inputValue,
  })

  const items = useMemo(() => {
    return options.map((optionItem) => ({
      value: optionItem.locationRef,
      label: optionItem.formattedAddress,
    }))
  }, [options])

  const emptyLabel = useMemo(
    () => (inputValue ? translate('locations.search_not_found') : translate('locations.search_placeholder')),
    [inputValue, translate]
  )

  return (
    <Search
      id="address-search-selector"
      items={items}
      selectedItem={selectedItem}
      label={label}
      disabled={disabled}
      required={!manualMode ? required : false}
      searchonly={true}
      loading={isLoading}
      emptyLabel={emptyLabel}
      onSearchValueChange={setInputValue}
      onSelect={(selectedItemValue) => {
        setSelectedItem(items.find((item) => item.value === selectedItemValue))
        onAddressSearchChange(options.find((option) => option.locationRef === selectedItemValue) ?? null)
      }}
      state={!errorMessage ? undefined : 'negative'}
      ref={searchFieldRef as LegacyRef<HTMLButtonElement | HTMLInputElement> | undefined}
      onClean={() => {
        setInputValue('')
        setSelectedItem(undefined)
        onAddressSearchChange(null)
      }}
      customAction={
        <Cluster y="center" x="center" fullWidth>
          <Button
            variant="ghost"
            onMouseDown={() => {
              setInputValue('')
              setSelectedItem(undefined)
              onAddManualAddress()
            }}
            disabled={manualMode}
          >
            <Cluster gap={4} y="center" x="center" fullWidth>
              <PlusIcon />
              {translate('locations.search_address_manual_button')}
            </Cluster>
          </Button>
        </Cluster>
      }
      hint={errorMessage}
    />
  )
}
